import { Language } from '@yleisradio/areena-types';
import { MediaLanguageCode } from '../appTypings';
import { logError } from '../error/errorUtils';
import { isoDurationToSeconds } from '../utils/duration';
import { uiLanguageToSnowplowLanguage } from './snowplowUtils';

const programContextGenerator = (programId: string): AnalyticsContext => ({
  schema: 'iglu:fi.yle.content/program/jsonschema/2-0-1',
  data: {
    program_id: programId,
    predicate: 'media_playing',
  },
});

const serviceContextGenerator = (service_id: string) => ({
  schema: 'iglu:fi.yle.content/service/jsonschema/2-0-1',
  data: {
    service_id,
    predicate: 'media_playing',
  },
});

const liveMediaContextGenerator = (dvrWindow: string): AnalyticsContext => ({
  schema: 'iglu:fi.yle.player/live_stream/jsonschema/1-0-0',
  data: { dvr_window: isoDurationToSeconds(dvrWindow) },
});

const mediaContextGenerator = (media_id: string) => ({
  schema: 'iglu:fi.yle.content/media/jsonschema/2-1-1',
  data: {
    media_id,
    predicate: 'media_playing',
  },
});

export type ContextServiceLanguage = 'fin' | 'swe';
export type ContextContentLanguage = MediaLanguageCode | 'und' | 'mul';

type AnalyticsContextParams = {
  section: string | undefined;
  service_language: ContextServiceLanguage;
  content_language: ContextContentLanguage;
  context_index: number;
};

export const yleContextGenerator = ({
  section,
  service_language,
  content_language,
  context_index,
}: AnalyticsContextParams) => ({
  schema: 'iglu:fi.yle/yle_context/jsonschema/2-3-0',
  data: {
    main_service: 'yle-areena',
    service: 'areena-leanback',
    section: section || 'page',
    service_language,
    content_language,
    context_index,
    wrapper_type: 'web',
  },
});

const publicationContextGenerator = (publication_id: string) => ({
  schema: 'iglu:fi.yle.content/publication/jsonschema/2-0-0',
  data: {
    publication_id,
    predicate: 'media_playing',
  },
});

type MediaTrackingContextGeneratorParams = {
  section: string | undefined;
  service_language: Language;
  content_language: ContextContentLanguage;
  isLiveMedia: boolean;
  dvr_window: string | undefined;
  programId: string;
  service_id: string | undefined;
  media_id: string;
  publication_id: string | undefined;
};

export type AnalyticsContext = {
  schema: string;
  data: Record<string, unknown>;
};

export const mediaTrackingGenerator = ({
  isLiveMedia,
  dvr_window,
  programId,
  section,
  service_language,
  content_language,
  service_id,
  media_id,
  publication_id,
}: MediaTrackingContextGeneratorParams): AnalyticsContext[] => {
  const context: AnalyticsContext[] = [];

  context.push(
    yleContextGenerator({
      section: section || 'page',
      service_language: uiLanguageToSnowplowLanguage(service_language),
      content_language,
      context_index: 1,
    })
  );

  if (isLiveMedia && dvr_window) {
    try {
      context.push(liveMediaContextGenerator(dvr_window));
    } catch (error) {
      logError(error);
    }
  }

  if (service_id) {
    context.push(serviceContextGenerator(service_id));
  }

  context.push(programContextGenerator(programId));

  if (media_id) {
    context.push(mediaContextGenerator(media_id));
  }
  if (publication_id) {
    context.push(publicationContextGenerator(publication_id));
  }

  return context;
};

export const activeEntityContextGenerator = (
  areena_view_id: string | undefined,
  version: string | undefined
) => ({
  schema: 'iglu:fi.yle.content/areena_view/jsonschema/2-1-0',
  data: {
    areena_view_id,
    version,
    predicate: 'consumed_activity',
  },
});

export const engagementViewContextGenerator = (engagement_view_id: string) => ({
  schema: 'iglu:fi.yle.engagement/view/jsonschema/2-1-0',
  data: {
    view_id: engagement_view_id,
    view_type: 'entity',
    view_entity_type: 'entity',
    view_mode: 'thumbnail',
  },
});

type GroupContextParams = {
  group_type: string | null;
  group_num_items: number | null;
  source_ref: string | null;
  source_url: string | null;
  group_label: string | null;
};

export const groupContextGenerator = ({
  group_type,
  group_num_items,
  source_ref,
  source_url,
  group_label,
}: GroupContextParams) => ({
  schema: 'iglu:fi.yle.ui/group/jsonschema/2-2-0',
  data: {
    type: group_type,
    num_items: group_num_items,
    source_ref,
    source_url,
    label: group_label,
    is_paginated: false,
  },
});

export const wrapperContextGenerator = (type: string, horizontal: boolean) => ({
  schema: 'iglu:fi.yle.ui/wrapper/jsonschema/2-0-0',
  data: {
    type: 'list',
    horizontal,
  },
});

export const tabContextGenerator = (title: string) => ({
  schema: 'iglu:fi.yle.ui/tab/jsonschema/2-0-0',
  data: {
    title,
  },
});

type UiElementContextGeratorParams = {
  type: string;
  layout_size: string | null;
  group_index: number;
  group_order_index: number;
  wrapper_index: number;
  title: string;
};

export const uiElementContextGenerator = ({
  type,
  layout_size,
  group_index,
  group_order_index,
  wrapper_index,
  title,
}: UiElementContextGeratorParams) => ({
  schema: 'iglu:fi.yle.ui/element/jsonschema/2-1-0',
  data: {
    type,
    layout_size,
    group_index,
    group_order_index,
    wrapper_index,
    title,
  },
});

export const imageContextGenerator = (image_id: string | null) => ({
  schema: 'iglu:fi.yle.content/image/jsonschema/2-0-0',
  data: {
    image_id,
    predicate: 'engagement_view_source',
  },
});

export const packageContextGenerator = (areena_package_id: string) => ({
  schema: 'iglu:fi.yle.content/areena_package/jsonschema/2-0-0',
  data: {
    areena_package_id,
    predicate: 'engagement_view_source',
  },
});

export const entityContextGenerator = (entity_id: string) => ({
  schema: 'iglu:fi.yle.content/entity/jsonschema/2-0-1',
  data: {
    entity_id,
    predicate: 'engagement_view_source',
  },
});

export const recommendationContextGenerator = (
  recommendation_id: string,
  method: string
) => ({
  schema: 'iglu:fi.yle.content/recommendation/jsonschema/2-0-0',
  data: {
    recommendation_id,
    method,
    predicate: 'engagement_view_source',
  },
});

export const abTestContextGenerator = (name: string, group: string) => ({
  schema: 'iglu:fi.yle.content/ab_test/jsonschema/2-0-0',
  data: {
    name,
    group,
  },
});

export const engagementViewEventGenerator = (view_id: string) => ({
  schema: 'iglu:fi.yle.engagement/view/jsonschema/2-1-0',
  data: {
    view_id,
    view_type: 'entity',
    view_entity_type: 'entity',
    view_mode: 'thumbnail',
  },
});

type consentContextGeneratorParams = {
  version: string;
  development: boolean;
  personalization: boolean;
  embedded_social_media: boolean;
  targeting: boolean;
};

export const consentContextGenerator = ({
  version,
  development,
  personalization,
  embedded_social_media,
  targeting,
}: consentContextGeneratorParams) => ({
  schema: 'iglu:fi.yle/consent/jsonschema/2-0-1',
  data: {
    version,
    development,
    personalization,
    embedded_social_media,
    targeting,
  },
});

export const userProfileContextGenerator = (age_group: number | undefined) => ({
  schema: 'iglu:fi.yle.content/user_profile/jsonschema/2-0-0',
  data: {
    age_group,
    predicate: 'consumer',
  },
});
